import { inject, Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";
import { FilterCategory, FilterCategoryType, Filter } from "./compact-filter.component";

@Injectable({
  providedIn: "root",
})
export class CompactFilterService {
  private readonly translocoService = inject(TranslocoService);

  readonly Targetings = "sd-strategy-creation.targetings";
  readonly AdvertisedProducts = "sd-strategy-creation.advertised_products";

  public readonly FILTERS: {
    [key in FilterCategory]: Partial<Filter<FilterCategory>>;
  } = {
    [FilterCategory.Marketplace]: {
      id: "marketplace",
      type: FilterCategory.Marketplace,
      label: this.translocoService.translate("common.marketplace"),
      placeholder: "Filter by Marketplace",
      unique: true,
      searchable: true,
    },
    [FilterCategory.AccountGroup]: {
      id: "accountGroup",
      type: FilterCategory.AccountGroup,
      label: this.translocoService.translate("agency-board.account_group"),
      placeholder: "Filter by Account Group",
      searchable: true,
    },
    [FilterCategory.AccountBidderStatus]: {
      id: "accountBidderStatus",
      type: FilterCategory.AccountBidderStatus,
      label: this.translocoService.translate("sharedReport.account_status"),
      placeholder: "Filter by Account Status",
    },
    [FilterCategory.AccountType]: {
      id: "accountType",
      type: FilterCategory.AccountType,
      label: this.translocoService.translate("account-setting.type"),
      placeholder: "Filter by Account Type",
    },
    [FilterCategory.ProductGroup]: {
      id: "productGroup",
      type: FilterCategory.ProductGroup,
      label: this.translocoService.translate("product-group-bulk-upload-result-modal.product_group"),
      placeholder: "Filter by Product Group",
      category: this.translocoService.translate(this.AdvertisedProducts),
      searchable: true,
    },
    [FilterCategory.KeywordSegment]: {
      id: "keywordSegment",
      type: FilterCategory.KeywordSegment,
      label: this.translocoService.translate("keyword-segment-modal.keyword_segment"),
      placeholder: "Filter by Keyword Segment",
      category: this.translocoService.translate(this.Targetings),
      excludeModeAllowed: true,
      searchable: true,
    },
    [FilterCategory.ProductSegment]: {
      id: "productSegment",
      type: FilterCategory.ProductSegment,
      label: this.translocoService.translate("product-segment-modal.product_segment"),
      placeholder: "Filter by Product Segment",
      excludeModeAllowed: true,
      category: this.translocoService.translate(this.Targetings),
      searchable: true,
    },
    [FilterCategory.BrandTraffic]: {
      id: "brandTraffic",
      type: FilterCategory.BrandTraffic,
      label: this.translocoService.translate("brand-traffic.brand_traffic"),
      placeholder: "Filter by Brand Traffic",
      category: this.translocoService.translate(this.Targetings),
      excludeModeAllowed: true,
      searchable: true,
      selectAllOption: true,
    },
    [FilterCategory.Strategy]: {
      id: "strategy",
      type: FilterCategory.Strategy,
      label: this.translocoService.translate("common.strategy"),
      placeholder: "Filter by Strategy",
      category: this.translocoService.translate(this.AdvertisedProducts),
      searchable: true,
    },
    [FilterCategory.BrandProducts]: {
      id: "brandProducts",
      type: FilterCategory.BrandProducts,
      label: this.translocoService.translate("sov-main-table.brands"),
      placeholder: "Filter by Brand Products",
      category: this.translocoService.translate(this.AdvertisedProducts),
      searchable: true,
    },
    [FilterCategory.Brand]: {
      id: "brand",
      type: FilterCategory.Brand,
      label: this.translocoService.translate("sov-main-table.brands"),
      placeholder: "Filter by Brand",
      searchable: true,
    },
    [FilterCategory.ProductTag1]: {
      id: "productTag1",
      type: FilterCategory.ProductTag1,
      label: this.translocoService.translate("custom-field-edition.custom_field_1"),
      placeholder: "Filter by Product Tag 1",
      category: this.translocoService.translate(this.AdvertisedProducts),
      searchable: true,
    },
    [FilterCategory.ProductTag2]: {
      id: "productTag2",
      type: FilterCategory.ProductTag2,
      label: this.translocoService.translate("custom-field-edition.custom_field_2"),
      placeholder: "Filter by Product Tag 2",
      category: this.translocoService.translate(this.AdvertisedProducts),
      searchable: true,
    },
    [FilterCategory.DspCampaign]: {
      id: "dspCampaign",
      type: FilterCategory.DspCampaign,
      label: this.translocoService.translate("dsp-stats.filter_dsp_orders_label"),
      placeholder: "Filter by DSP Campaign",
      searchable: true,
    },
    [FilterCategory.DspCreative]: {
      id: "dspCreative",
      type: FilterCategory.DspCreative,
      label: this.translocoService.translate("dsp-stats.filter_dsp_creatives_label"),
      placeholder: "Filter by DSP Creative",
      searchable: true,
    },
    [FilterCategory.Bulk]: {
      id: "bulk",
      type: FilterCategory.Bulk,
      label: this.translocoService.translate("common.bulk"),
      placeholder: "Filter by Bulk",
      searchable: true,
    },
    [FilterCategory.AdType]: {
      id: "adType",
      type: FilterCategory.AdType,
      label: this.translocoService.translate("common.ad_type"),
      placeholder: "Filter by Ad Type",
      localStorageKey: "adType_filters",
      unique: true,
    },
    [FilterCategory.DspFunnel]: {
      id: "dspFunnel",
      type: FilterCategory.DspFunnel,
      label: this.translocoService.translate("strategy-page.funnel"),
      placeholder: "Filter by Funnel",
    },
    [FilterCategory.DspDealSource]: {
      id: "dspDealSource",
      type: FilterCategory.DspDealSource,
      label: this.translocoService.translate("dsp-media-selector.source"),
      placeholder: "Filter by Source",
    },
    [FilterCategory.DspDealType]: {
      id: "dspDealType",
      type: FilterCategory.DspDealType,
      label: this.translocoService.translate("dsp-media-selector.deal_type"),
      placeholder: "Filter by Deal Type",
    },
  };

  public getFilterWithOptions<T extends FilterCategory>(
    type: T,
    options: FilterCategoryType[T][],
    existingFilter?: Partial<Filter<T>>,
  ): Filter<T> {
    const filter = this.FILTERS[type];
    return { ...filter, options: () => options, ...existingFilter } as unknown as Filter<T>;
  }

  public findFilterWithType<T extends FilterCategory>(
    filters: Filter<FilterCategory>[],
    category: T,
  ): Filter<T> | undefined {
    return filters.find((f): f is Filter<T> => f.type === category);
  }
}
